/**
 * Stage Plot Icon Bank — Part B (front-view clip-art pictograms)
 *
 * CONVENTION (DO NOT BREAK):
 *  - viewBox is exactly "0 0 64 64"
 *  - body markup uses fill="currentColor" so the wrapper <svg>'s
 *    `color` (selected palette colour) recolours it live.
 *  - outlines / neutral negative-space detail use stroke="#0004" or "#0003".
 *  - Style: FRONT-VIEW, normalized, black-on-white line-art pictograms.
 *    Instruments/gear are viewed face-on (not top-down schemes):
 *    solid silhouette fills mixed with thin outline strokes, minimal but
 *    recognizable internal detail, thin metallic stands drawn with fine lines.
 * @see stageIconBankA.ts for the shared authoring convention.
 */

export const STAGE_ICON_BANK_B: Record<string, string> = {
  // ===================== KEYS =====================
  // Front view: you look down at the keyboard face; white keys with black keys.
  'stage-piano': `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="4" y="16" width="56" height="14" rx="2" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="6" y="30" width="52" height="20" rx="2" fill="#fff" stroke="#0004" stroke-width="2"/>
    <path d="M14 30 V50 M22 30 V50 M30 30 V50 M38 30 V50 M46 30 V50 M54 30 V50" stroke="#0004" stroke-width="1"/>
    <rect x="11" y="30" width="4" height="12" fill="currentColor"/>
    <rect x="19" y="30" width="4" height="12" fill="currentColor"/>
    <rect x="35" y="30" width="4" height="12" fill="currentColor"/>
    <rect x="43" y="30" width="4" height="12" fill="currentColor"/>
    <rect x="51" y="30" width="4" height="12" fill="currentColor"/>
    <rect x="10" y="20" width="10" height="4" rx="1" fill="#0004"/>
  </svg>`,

  synth: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="4" y="14" width="56" height="22" rx="2" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="13" cy="21" r="3" fill="#fff" stroke="#0004" stroke-width="1"/>
    <circle cx="23" cy="21" r="3" fill="#fff" stroke="#0004" stroke-width="1"/>
    <rect x="34" y="18" width="22" height="3" rx="1.5" fill="#fff"/>
    <rect x="34" y="26" width="22" height="3" rx="1.5" fill="#fff"/>
    <rect x="6" y="36" width="52" height="14" rx="2" fill="#fff" stroke="#0004" stroke-width="2"/>
    <path d="M14 36 V50 M22 36 V50 M30 36 V50 M38 36 V50 M46 36 V50 M54 36 V50" stroke="#0004" stroke-width="1"/>
    <rect x="11" y="36" width="4" height="8" fill="currentColor"/>
    <rect x="27" y="36" width="4" height="8" fill="currentColor"/>
    <rect x="43" y="36" width="4" height="8" fill="currentColor"/>
  </svg>`,

  organ: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="6" y="12" width="52" height="16" rx="2" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="10" y="16" width="3" height="8" fill="#fff"/>
    <rect x="15" y="16" width="3" height="8" fill="#fff"/>
    <rect x="20" y="16" width="3" height="8" fill="#fff"/>
    <rect x="25" y="16" width="3" height="8" fill="#fff"/>
    <rect x="30" y="16" width="3" height="8" fill="#fff"/>
    <rect x="6" y="28" width="52" height="12" rx="1" fill="#fff" stroke="#0004" stroke-width="2"/>
    <path d="M14 28 V40 M22 28 V40 M30 28 V40 M38 28 V40 M46 28 V40 M54 28 V40" stroke="#0004" stroke-width="1"/>
    <rect x="6" y="41" width="52" height="10" rx="1" fill="#fff" stroke="#0004" stroke-width="2"/>
    <path d="M14 41 V51 M22 41 V51 M30 41 V51 M38 41 V51 M46 41 V51 M54 41 V51" stroke="#0004" stroke-width="1"/>
  </svg>`,

  keytar: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="8" y="24" width="30" height="16" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M14 24 V40 M20 24 V40 M26 24 V40 M32 24 V40" stroke="#fff" stroke-width="1"/>
    <rect x="12" y="24" width="3" height="9" fill="#fff"/>
    <rect x="24" y="24" width="3" height="9" fill="#fff"/>
    <path d="M38 32 H52 C56 32 58 30 58 26 L58 20" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <circle cx="46" cy="34" r="4" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <path d="M52 40 L58 46" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  keystand: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="12" y="18" width="40" height="8" rx="2" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M22 26 L14 54 M42 26 L50 54" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M18 40 L46 40" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M10 56 H26 M38 56 H54" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  // ===================== VOCALS =====================
  // Front view: microphone standing upright, capsule at top.
  'vocal-mic': `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="26" y="30" width="12" height="22" rx="4" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M23 54 H41" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <path d="M22 14 a10 10 0 0 1 20 0 V30 a10 10 0 0 1 -20 0 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M22 18 H42 M22 22 H42 M22 26 H42" stroke="#0004" stroke-width="1.2"/>
    <rect x="29" y="30" width="6" height="3" fill="#0004"/>
  </svg>`,

  condenser: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="26" y="34" width="12" height="18" rx="3" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M23 54 H41" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <rect x="22" y="8" width="20" height="28" rx="6" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="26" y="12" width="12" height="18" rx="4" fill="none" stroke="#0004" stroke-width="1.2"/>
    <path d="M26 16 H38 M26 21 H38 M26 26 H38" stroke="#0004" stroke-width="1"/>
  </svg>`,

  ribbon: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="26" y="36" width="12" height="16" rx="3" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M23 54 H41" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <rect x="23" y="8" width="18" height="30" rx="9" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="27" y="14" width="10" height="18" rx="2" fill="none" stroke="#0004" stroke-width="1.2"/>
    <path d="M27 18 H37 M27 22 H37 M27 26 H37 M27 30 H37" stroke="#0004" stroke-width="1"/>
  </svg>`,

  singer: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <circle cx="28" cy="16" r="8" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M14 52 C14 40 21 32 28 32 C35 32 42 40 42 52 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M42 42 L48 22" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <circle cx="50" cy="18" r="6" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M46 16 H54 M46 20 H54" stroke="#0004" stroke-width="1"/>
  </svg>`,

  iem: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M32 14 C22 14 18 22 18 30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <path d="M32 14 C42 14 46 22 46 30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <path d="M18 30 C16 40 20 46 22 50" fill="none" stroke="#0004" stroke-width="1.5"/>
    <path d="M46 30 C48 40 44 46 42 50" fill="none" stroke="#0004" stroke-width="1.5"/>
    <circle cx="18" cy="32" r="6" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <circle cx="46" cy="32" r="6" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <circle cx="18" cy="32" r="2" fill="#fff"/>
    <circle cx="46" cy="32" r="2" fill="#fff"/>
  </svg>`,

  headphones: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M14 38 C14 18 50 18 50 38" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round"/>
    <rect x="8" y="34" width="14" height="20" rx="5" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <rect x="42" y="34" width="14" height="20" rx="5" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <rect x="12" y="38" width="6" height="12" rx="3" fill="#0004"/>
    <rect x="46" y="38" width="6" height="12" rx="3" fill="#0004"/>
  </svg>`,

  // ===================== STAGE =====================
  // Front view: floor wedge angled toward you, driver visible.
  wedge: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M8 50 L56 50 L50 24 L14 24 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="37" r="10" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="37" r="4" fill="#fff"/>
    <path d="M12 46 H52" stroke="#0004" stroke-width="1.5"/>
  </svg>`,

  sidefill: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="12" y="6" width="40" height="52" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="24" r="13" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="24" r="5" fill="#fff"/>
    <circle cx="32" cy="46" r="7" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="46" r="2.5" fill="#fff"/>
  </svg>`,

  monitor: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M10 52 L54 52 L48 22 L16 22 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="38" r="9" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="38" r="3.5" fill="#fff"/>
    <ellipse cx="32" cy="27" rx="8" ry="2.5" fill="#0004"/>
  </svg>`,

  sub: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="8" y="8" width="48" height="48" rx="4" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="32" r="17" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="32" r="11" fill="none" stroke="#0004" stroke-width="1.5"/>
    <circle cx="32" cy="32" r="5" fill="#fff" stroke="#0004" stroke-width="1.5"/>
  </svg>`,

  linearray: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M32 4 V10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M22 12 L42 12 L44 24 L20 24 Z" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M20 26 L44 26 L46 38 L18 38 Z" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M18 40 L46 40 L48 52 L16 52 Z" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <circle cx="32" cy="18" r="3.5" fill="#fff"/>
    <circle cx="32" cy="32" r="4" fill="#fff"/>
    <circle cx="32" cy="46" r="4.5" fill="#fff"/>
  </svg>`,

  paspeaker: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="12" y="6" width="40" height="52" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="22" r="12" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="22" r="4.5" fill="#fff"/>
    <circle cx="32" cy="45" r="7" fill="none" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="45" r="2.5" fill="#fff"/>
  </svg>`,

  outlet: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="14" y="10" width="36" height="44" rx="5" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="32" cy="32" r="13" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="28" y="21" width="3" height="7" rx="1.5" fill="currentColor"/>
    <rect x="33" y="21" width="3" height="7" rx="1.5" fill="currentColor"/>
    <circle cx="32" cy="40" r="3" fill="currentColor"/>
  </svg>`,

  distro: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="8" y="12" width="48" height="40" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="20" cy="24" r="5" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <circle cx="44" cy="24" r="5" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="14" y="38" width="12" height="8" rx="1.5" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="38" y="38" width="12" height="8" rx="1.5" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <circle cx="32" cy="24" r="2" fill="#fff"/>
  </svg>`,

  generator: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M14 18 H50 V14 H14 Z" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <rect x="8" y="18" width="48" height="34" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="13" y="24" width="20" height="16" rx="2" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <path d="M17 32 H29 M23 26 V38" stroke="#0004" stroke-width="1.5"/>
    <circle cx="44" cy="30" r="7" fill="none" stroke="#0004" stroke-width="1.5"/>
    <circle cx="44" cy="30" r="2" fill="#fff"/>
    <rect x="13" y="44" width="38" height="4" rx="1" fill="#0004"/>
  </svg>`,

  truss: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M6 22 H58 M6 42 H58" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M6 22 L18 42 L30 22 L42 42 L54 22 M6 42 L18 22 L30 42 L42 22 L54 42" stroke="currentColor" stroke-width="2" fill="none"/>
    <circle cx="6" cy="22" r="2.5" fill="currentColor"/>
    <circle cx="58" cy="22" r="2.5" fill="currentColor"/>
    <circle cx="6" cy="42" r="2.5" fill="currentColor"/>
    <circle cx="58" cy="42" r="2.5" fill="currentColor"/>
  </svg>`,

  'light-fixture': `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M32 6 V14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M20 14 H44 L38 26 H26 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M26 26 L38 26 L44 46 L20 46 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <ellipse cx="32" cy="46" rx="12" ry="4" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <ellipse cx="32" cy="46" rx="6" ry="2" fill="#0004"/>
  </svg>`,

  par: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M20 16 L44 16 L40 42 L24 42 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <ellipse cx="32" cy="16" rx="12" ry="4" fill="#fff" stroke="#0004" stroke-width="2"/>
    <ellipse cx="32" cy="16" rx="6" ry="2" fill="#0004"/>
    <rect x="26" y="42" width="12" height="8" rx="1.5" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M32 50 V58 M24 58 H40" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  movinghead: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="18" y="46" width="28" height="10" rx="2" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <rect x="24" y="36" width="16" height="10" rx="2" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <path d="M20 14 L44 14 L40 36 L24 36 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <ellipse cx="32" cy="14" rx="12" ry="4" fill="#fff" stroke="#0004" stroke-width="2"/>
    <ellipse cx="32" cy="14" rx="6" ry="2" fill="#0004"/>
  </svg>`,

  micstand: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="28" y="6" width="8" height="14" rx="4" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M32 20 V52" stroke="currentColor" stroke-width="3"/>
    <path d="M18 24 H46" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M20 52 L14 60 M44 52 L50 60 M32 52 V60" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M14 60 H50" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  dibox: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <rect x="10" y="16" width="44" height="32" rx="3" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <circle cx="20" cy="26" r="4" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <circle cx="44" cy="26" r="4" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="16" y="36" width="14" height="6" rx="1" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="34" y="36" width="14" height="6" rx="1" fill="#fff" stroke="#0004" stroke-width="1.5"/>
    <rect x="28" y="22" width="8" height="8" rx="1" fill="#0004"/>
  </svg>`,

  cable: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M10 40 C10 24 22 24 22 32 C22 40 34 40 34 30 C34 20 46 20 48 30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
    <rect x="6" y="38" width="8" height="10" rx="2" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <rect x="46" y="24" width="6" height="12" rx="2" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <path d="M52 28 H56 M52 32 H56" stroke="#0004" stroke-width="1.5"/>
  </svg>`,

  // Two rack toms clamped to ONE shared stand (front view). Each tom is a
  // drum shell — an oval head (with the inner head ring) on a short cylinder
  // body with vertical tension rods — held by two short mounting arms on a
  // single centre post that drops to a splayed tripod base. Drawn as bank
  // SVG (no StagePlotPro PNG exists for this configuration).
  'rack-toms-stand': `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M4 17 V35 a12 5 0 0 0 24 0 V17 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <ellipse cx="16" cy="17" rx="12" ry="5" fill="#fff" stroke="#0004" stroke-width="2"/>
    <ellipse cx="16" cy="17" rx="7" ry="3" fill="none" stroke="#0004" stroke-width="1"/>
    <path d="M8 21 V33 M12.5 21 V35 M19.5 21 V35 M24 21 V33" stroke="#0004" stroke-width="1"/>
    <path d="M36 17 V35 a12 5 0 0 0 24 0 V17 Z" fill="currentColor" stroke="#0004" stroke-width="2"/>
    <ellipse cx="48" cy="17" rx="12" ry="5" fill="#fff" stroke="#0004" stroke-width="2"/>
    <ellipse cx="48" cy="17" rx="7" ry="3" fill="none" stroke="#0004" stroke-width="1"/>
    <path d="M40 21 V33 M44.5 21 V35 M51.5 21 V35 M56 21 V33" stroke="#0004" stroke-width="1"/>
    <path d="M15 31 L30 35 M49 31 L34 35" fill="none" stroke="#0004" stroke-width="2"/>
    <rect x="30" y="34" width="4" height="15" fill="currentColor" stroke="#0004" stroke-width="1.5"/>
    <circle cx="32" cy="49" r="2.5" fill="currentColor" stroke="#0004" stroke-width="1"/>
    <path d="M32 49 L19 61 M32 49 L45 61 M32 49 V61" fill="none" stroke="#0004" stroke-width="2"/>
  </svg>`,

  // Splash cymbal — TOP-DOWN (plan view), NO stand: a small cymbal disc with
  // lathe rings and a small bell. Drawn deliberately SMALLER than the standard
  // cymbal so the two are told apart at palette size. No StagePlotPro asset
  // exists for a splash (the drum/ folder only ships cymbal/cymbalLA/cymbalRA).
  splash: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <circle cx="32" cy="32" r="20.5" fill="currentColor" stroke="#0004" stroke-width="1.6"/>
    <circle cx="32" cy="32" r="16" fill="none" stroke="#fff" stroke-opacity="0.6" stroke-width="0.9"/>
    <circle cx="32" cy="32" r="11" fill="none" stroke="#fff" stroke-opacity="0.6" stroke-width="0.9"/>
    <circle cx="32" cy="32" r="6.5" fill="#fff" fill-opacity="0.9" stroke="#0004" stroke-width="1.1"/>
    <circle cx="32" cy="32" r="2.2" fill="#0004"/>
  </svg>`,

  // China cymbal — TOP-DOWN (plan view), NO stand. The signature of a china is
  // its FLANGED, upturned edge: seen from above it reads as a distinctly
  // ANGULAR (octagonal) disc, unlike the round crash/ride/splash — that is what
  // tells it apart at a glance. Big bell. No StagePlotPro asset exists.
  china: `<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M32 4 L54 15 L59 38 L44 59 L18 58 L5 38 L11 15 Z" fill="currentColor" stroke="#0004" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M32 9.5 L49 18 L53 36 L41 53 L20 52 L11 36 L16 18 Z" fill="none" stroke="#fff" stroke-opacity="0.6" stroke-width="0.9"/>
    <circle cx="32" cy="32" r="8" fill="#fff" fill-opacity="0.9" stroke="#0004" stroke-width="1.1"/>
    <circle cx="32" cy="32" r="2.8" fill="#0004"/>
  </svg>`,
};
